import React, { useEffect, useMemo, useState } from "react"
import { Checkbox, Collapse, Input, InputNumber } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { useCatalogueSources } from "features/TabSpec/api/useCatalogueSources"
import { CatalogueQuestion } from "features/TabSpec/types/catalogue"
import { DummyProfile, DummyQuestionSettings } from "types/dummyData.types"
import {
  MAX_PROFILE_NUMBER,
  numbersProblem,
  profileQuestions,
  removeQuestion,
  setQuestion,
  sharePreview,
  textsFromInput,
  unusedLabels,
} from "../model/profile"

interface Props {
  projectId: string
  owner: string
  profile: DummyProfile
  onChange: (profile: DummyProfile) => void
  /** Every editor is read-only (while a run is generating). */
  disabled?: boolean
}

interface EditorProps {
  q: CatalogueQuestion
  profile: DummyProfile
  onChange: (profile: DummyProfile) => void
  disabled: boolean
}

const SINGLE = ["select-one", "range", "grid-one", "grid-range"]
const MULTI = ["select-many", "grid-many"]
const NUMBER = ["number", "grid-number"]

const settingsOf = (profile: DummyProfile, label: string): DummyQuestionSettings =>
  profile.questions?.[label] ?? {}

/** A copy of `values` with `key` set to `v`, or without it when `v` is empty. */
const withValue = (
  values: Record<string, number> | undefined,
  key: string,
  v: number | null
): Record<string, number> => {
  const next = { ...(values ?? {}) }
  if (v === null || v === undefined) delete next[key]
  else next[key] = v
  return next
}

/**
 * One answer per respondent: a share per option, its percentage as the
 * generator will use it, and exact counts where the generator can keep them
 * (a single-select outside grids and loops).
 */
const SingleEditor: React.FC<EditorProps> = ({ q, profile, onChange, disabled }) => {
  const s = settingsOf(profile, q.label)
  const preview = sharePreview(q.options, s.shares)
  const canBeExact =
    (q.type === "select-one" || q.type === "range") && q.instances.length <= 1
  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 text-xs text-gray-500">
        Give an answer a number to set its share. Answers left empty share what
        is left of 100.
      </p>
      {q.options.map((o) => (
        <label key={o.label} className="flex items-center gap-2 text-sm">
          <span className="w-48 truncate" title={o.label}>
            {o.label}
          </span>
          <InputNumber
            aria-label={`${q.label} ${o.label} share`}
            min={0}
            disabled={disabled}
            value={s.shares?.[o.label] ?? null}
            onChange={(v) =>
              onChange(
                setQuestion(profile, q.label, {
                  shares: withValue(s.shares, o.label, v),
                })
              )
            }
          />
          <span className="text-xs text-gray-500">{preview[o.label]}%</span>
        </label>
      ))}
      {q.options.some((o) => o.exception) && (
        <p className="m-0 text-xs text-gray-500">
          Exception answers, such as Don&apos;t know, are picked now and then at
          random unless you give one a share. The percentages leave those
          random picks out.
        </p>
      )}
      {canBeExact && (
        <Checkbox
          checked={!!s.exact}
          disabled={disabled}
          onChange={(e) =>
            onChange(
              setQuestion(profile, q.label, {
                exact: e.target.checked || undefined,
              })
            )
          }
        >
          Exact counts: each answer gets exactly its share of respondents. Only
          for a question everyone is asked.
        </Checkbox>
      )}
    </div>
  )
}

/** Several answers per respondent: the chance of each ordinary option being picked. */
const MultiEditor: React.FC<EditorProps> = ({ q, profile, onChange, disabled }) => {
  const s = settingsOf(profile, q.label)
  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 text-xs text-gray-500">
        Each answer is picked on its own. Answers left empty have a 33% chance.
      </p>
      {q.options
        .filter((o) => !o.exception)
        .map((o) => (
          <label key={o.label} className="flex items-center gap-2 text-sm">
            <span className="w-48 truncate" title={o.label}>
              {o.label}
            </span>
            <InputNumber
              aria-label={`${q.label} ${o.label} chance`}
              min={0}
              max={100}
              placeholder="33"
              disabled={disabled}
              value={s.chance?.[o.label] ?? null}
              onChange={(v) =>
                onChange(
                  setQuestion(profile, q.label, {
                    chance: withValue(s.chance, o.label, v),
                  })
                )
              }
            />
            <span className="text-xs text-gray-500">% chance of being picked</span>
          </label>
        ))}
    </div>
  )
}

type NumberKey = "min" | "max" | "mean" | "spread"

/** A number answer: its limits, and an average and spread for a bell curve. */
const NumberEditor: React.FC<EditorProps> = ({ q, profile, onChange, disabled }) => {
  const s = settingsOf(profile, q.label)
  const problem = numbersProblem(s)
  const field = (key: NumberKey, label: string) => (
    <label className="flex flex-col text-sm">
      {label}
      <InputNumber
        aria-label={`${q.label} ${label}`}
        min={key === "spread" ? 0 : -MAX_PROFILE_NUMBER}
        max={MAX_PROFILE_NUMBER}
        disabled={disabled}
        value={s[key] ?? null}
        onChange={(v) => {
          const patch: Partial<DummyQuestionSettings> = {}
          patch[key] = v ?? undefined
          onChange(setQuestion(profile, q.label, patch))
        }}
      />
    </label>
  )
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-4">
        {field("min", "Minimum")}
        {field("max", "Maximum")}
        {field("mean", "Average")}
        {field("spread", "Spread")}
      </div>
      <p className="m-0 text-xs text-gray-500">
        Without an average or a spread, numbers are spread evenly between the
        minimum and the maximum.
      </p>
      {problem && (
        <p className="m-0 text-xs text-red-600" role="alert">
          {problem}
        </p>
      )}
    </div>
  )
}

const sameTexts = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((t, i) => t === b[i])

/**
 * Sample answers for a text question, one per line. The box keeps what the
 * user types (a new line included) and saves the lines that hold text.
 */
const TextsEditor: React.FC<EditorProps> = ({ q, profile, onChange, disabled }) => {
  const texts = settingsOf(profile, q.label).texts
  const [draft, setDraft] = useState(() => (texts ?? []).join("\n"))
  // A change from outside (Remove, a new run's profile) replaces the box;
  // the user's own typing already matches the profile and is left alone.
  useEffect(() => {
    setDraft((d) =>
      sameTexts(textsFromInput(d), texts ?? []) ? d : (texts ?? []).join("\n")
    )
  }, [texts])
  return (
    <Input.TextArea
      aria-label={`${q.label} sample answers`}
      rows={4}
      placeholder="One sample answer per line"
      disabled={disabled}
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value)
        onChange(
          setQuestion(profile, q.label, { texts: textsFromInput(e.target.value) })
        )
      }}
    />
  )
}

const EditorFor: React.FC<EditorProps> = (props) => {
  if (SINGLE.includes(props.q.type)) return <SingleEditor {...props} />
  if (MULTI.includes(props.q.type)) return <MultiEditor {...props} />
  if (NUMBER.includes(props.q.type)) return <NumberEditor {...props} />
  return <TextsEditor {...props} />
}

/**
 * The question's editor and, once it has settings, Clear settings: it also
 * clears what the editor does not show (shares per grid row, exact counts on
 * a grid or a loop, an option the question no longer has).
 */
const QuestionEditor: React.FC<EditorProps> = (props) => (
  <div className="flex flex-col gap-3">
    <EditorFor {...props} />
    {props.profile.questions?.[props.q.label] && (
      <div>
        <DataQButton
          size="small"
          isDisabled={props.disabled}
          onClick={() => props.onChange(removeQuestion(props.profile, props.q.label))}
        >
          Clear settings
        </DataQButton>
      </div>
    )}
  </div>
)

/**
 * "Customise answers": one editor per question of the questionnaire (every
 * variant), keyed by label in the profile, and the settings the profile
 * still holds for questions the questionnaire no longer has.
 */
const QuestionSettings: React.FC<Props> = ({
  projectId,
  owner,
  profile,
  onChange,
  disabled = false,
}) => {
  // The questions only: never the routing, a walk of the data on the server.
  const sources = useCatalogueSources(projectId, owner, null, true, false)
  const questions = useMemo(
    () => (sources.parse ? profileQuestions(sources.parse) : []),
    [sources.parse]
  )
  const unused = unusedLabels(
    profile,
    questions.map((q) => q.label)
  )

  if (sources.isLoading)
    return <p className="text-sm m-0">Reading the questionnaire...</p>
  if (sources.error) return <p className="text-sm m-0">{sources.error}</p>
  return (
    <div className="flex flex-col gap-3" data-testid="question-settings">
      {questions.length === 0 ? (
        <p className="text-sm m-0">
          The questionnaire has no questions to customise.
        </p>
      ) : (
        <Collapse
          items={questions.map((q) => ({
            key: q.label,
            label: `${q.label}${profile.questions?.[q.label] ? " (customised)" : ""}`,
            children: (
              <QuestionEditor
                q={q}
                profile={profile}
                onChange={onChange}
                disabled={disabled}
              />
            ),
          }))}
        />
      )}
      {unused.length > 0 && (
        <div className="flex flex-col gap-1 text-sm" data-testid="unused-settings">
          <p className="m-0">
            Settings for questions this questionnaire no longer has:
          </p>
          {unused.map((l) => (
            <div key={l} className="flex items-center gap-2">
              <span>{l}</span>
              <DataQButton
                size="small"
                isDisabled={disabled}
                onClick={() => onChange(removeQuestion(profile, l))}
              >
                Remove
              </DataQButton>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default QuestionSettings
